// The composer's attachment adapter: what happens between picking a file and
// it appearing in the message.
//
// The upload happens on ADD, not on send. Two reasons. The server has the last
// word on size and type, and finding that out while you're still composing is
// a chip that turns red; finding it out on send is a message that refuses to
// go. And a big file gets its wait up front instead of stalling the send.
//
// The cost is a file uploaded for a message that never gets sent, which is why
// remove() deletes it. Anything that slips past that (a closed tab) is reaped
// with the conversation, so there's still one clock.

import { deleteFile, downscaleImage, humanSize, uploadFile } from '../../services/files';
import { isImage } from '../../services/conversations';

// Everything except the shapes that are a security problem to hand back to a
// browser or useless to a model. Images are added to this only when the
// project has a model that can read one.
const DOCUMENT_ACCEPT = [
  '.pdf', '.txt', '.md', '.csv', '.json', '.yaml', '.yml', '.log',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
].join(',');

const IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

export function attachmentAccept(canSendImages) {
  return canSendImages ? `${DOCUMENT_ACCEPT},${IMAGE_ACCEPT}` : DOCUMENT_ACCEPT;
}

// createAttachmentAdapter builds the adapter for the current selection.
//
// ensureConversation is async and returns the id to upload into, creating the
// chat if the user attached before they typed. It's a callback rather than a
// value because at the time the adapter is built there is usually no chat yet.
export function createAttachmentAdapter({ limits, canSendImages, ensureConversation }) {
  // uploads still running, by chip id, so a send can wait for its file
  const inflight = new Map();
  // the chip id only has to be unique here; crypto.randomUUID would need a
  // secure context, and an install served over plain http doesn't have one
  let uploads = 0;

  const store = async (file, pending) => {
    const image = pending.type === 'image';

    // Downscale before the size check, or we'd refuse a photo we were about
    // to shrink below the limit anyway.
    const upload = image ? await downscaleImage(file) : file;

    const limit = image ? limits.maxImageBytes : limits.maxFileBytes;
    if (limit && upload.size > limit) {
      throw new Error(
        `${file.name} is ${humanSize(upload.size)}, over the ${humanSize(limit)} limit`,
      );
    }

    const conversationID = await ensureConversation();
    const stored = await uploadFile(conversationID, upload);

    return {
      ...pending,
      fileId: stored.id,
      name: stored.filename,
      contentType: stored.media_type,
      bytes: stored.bytes,
      file: upload,
      status: { type: 'requires-action', reason: 'composer-send' },
    };
  };

  return {
    accept: attachmentAccept(canSendImages),

    // A generator so the chip shows up the moment the file is dropped, as
    // uploading, rather than after the upload: assistant-ui draws every state
    // we yield, matched by id. That's why the id is ours and the server's goes
    // in fileId: there is no server id yet when the chip first appears.
    async *add({ file }) {
      const image = isImage(file.type);

      if (image && !canSendImages) {
        throw new Error('This project has no model that can read images');
      }

      const pending = {
        id: `upload-${++uploads}`,
        type: image ? 'image' : 'document',
        name: file.name,
        contentType: file.type,
        file,
        status: { type: 'running', reason: 'uploading', progress: 0 },
      };
      yield pending;

      const stored = store(file, pending);
      inflight.set(pending.id, stored);
      // until the upload settles, not until this generator ends: removing the
      // chip cancels the generator, and remove() still has to find the upload.
      // The catch also keeps a failure from being reported twice; the chip
      // gets it through the yield below.
      stored.catch(() => {}).finally(() => inflight.delete(pending.id));

      yield await stored;
    },

    // No content parts: a message part is how a file the ASSISTANT produced
    // gets rendered, and reusing it here makes an upload display as though the
    // model had returned it. What the user attached is shown from
    // message.attachments instead, which is what MessagePrimitive.Attachments
    // reads.
    // Sent while still uploading: assistant-ui waits for this, so the message
    // waits for its file instead of going without it.
    async send(attachment) {
      const ready = (await inflight.get(attachment.id)) ?? attachment;

      return { ...ready, status: { type: 'complete' }, content: [] };
    },

    async remove(attachment) {
      // removed while still uploading: the upload carries on regardless, so
      // wait for it and delete what it stored
      let fileId = attachment?.fileId;
      if (!fileId && inflight.has(attachment?.id)) {
        fileId = await inflight.get(attachment.id).then((a) => a.fileId, () => undefined);
      }
      if (!fileId) return;

      // A failed delete leaves a file the conversation's retention will take
      // care of, so it isn't worth stopping the user over.
      try {
        await deleteFile(fileId);
      } catch (err) {
        console.warn('could not remove the uploaded file:', err);
      }
    },
  };
}
